import React from "react";
import styled, { css } from "styled-components";
import { Button, rawColors as uiColors } from "@magicbi/ui-library";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";

/** `default`: a bare icon. `error`: the icon in a red-tinted circle. */
export type EmptyStateTone = "default" | "error";

export interface EmptyStateProps {
  /** Visual leading slot — an icon or a Spinner. */
  icon?: React.ReactNode;
  /** Optional heading above the caption ("No runs yet"). */
  title?: string;
  caption: string;
  /** Optional single action under the caption ("Send now"). */
  action?: { label: string; onClick: () => void; variant?: "primary" | "secondary"; disabled?: boolean } | null;
  /** Optional second action beside the first ("Back to the list"). */
  secondaryAction?: { label: string; onClick: () => void } | null;
  /** Announce the state to screen readers as an alert (an error that appears after load). */
  announce?: boolean;
  /** `error` draws the icon in a red-tinted circle with a semibold title. Default `default`. */
  tone?: EmptyStateTone;
  /** `roomy` gives the state a tall, centred card (about 310px) for a page-level error. Default `default`. */
  size?: "default" | "roomy";
  className?: string;
}

const Wrap = styled.div<{ $roomy: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  width: 100%;
  padding: ${spacing[6]} ${spacing[4]};
  ${({ $roomy }) =>
    $roomy &&
    css`
      min-height: 19.375rem; /* GAP: 310px error card */
    `}
`;

const IconSlot = styled.div<{ $tone: EmptyStateTone }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $tone }) => ($tone === "error" ? uiColors.accent.red500 : rawColors.text.lowEmphasis)};

  ${({ $tone }) =>
    $tone === "error"
      ? css`
          width: ${spacing[11]};
          height: ${spacing[11]};
          border-radius: 50%;
          background: ${uiColors.accent.red50};

          svg {
            width: ${spacing[5]};
            height: ${spacing[5]};
          }
        `
      : css`
          svg {
            width: 32px;
            height: 32px;
          }
        `}
`;

const Title = styled.h2<{ $tone: EmptyStateTone }>`
  margin: 0;
  ${typographyMixin("subtitle", 2)}
  ${({ $tone }) => ($tone === "error" ? "font-weight: 600;" : "")}
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing[2]};
`;

const Caption = styled.p<{ $tone: EmptyStateTone }>`
  margin: 0;
  ${typographyMixin("body", 4)}
  color: ${({ $tone, theme }) => ($tone === "error" ? theme.colors.text.secondary : rawColors.text.mediumEmphasis)};
  text-align: center;
`;

/**
 * EmptyState — centred icon/spinner + caption. Used for the wizard's
 * "Fetching schemas", "Select a schema to browse tables", and
 * "Select a table to preview its data" placeholders.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  caption,
  action = null,
  secondaryAction = null,
  announce = false,
  tone = "default",
  size = "default",
  className,
}) => (
  <Wrap $roomy={size === "roomy"} className={className} role={announce ? "alert" : undefined}>
    {icon ? <IconSlot $tone={tone}>{icon}</IconSlot> : null}
    {title ? <Title $tone={tone}>{title}</Title> : null}
    <Caption $tone={tone}>{caption}</Caption>
    {action || secondaryAction ? (
      <Actions>
        {action ? (
          <Button variant={action.variant ?? "secondary"} size="sm" onClick={action.onClick} disabled={action.disabled}>
            {action.label}
          </Button>
        ) : null}
        {secondaryAction ? (
          <Button variant="secondary" size="sm" onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </Button>
        ) : null}
      </Actions>
    ) : null}
  </Wrap>
);

export default EmptyState;
